import { z } from 'zod'

import { sentenceCase } from '@/lib/text'
import { blankToNull, knownWord, lenientList, moneySchema, percentSchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Signal Zone: the fifteen `/api/v5/signals/…` reads, parsed straight off
 * the wire into app shapes.
 *
 * Wire-to-app in one schema, rather than the `Wire<>` mapper + app schema pair
 * the older families use, because the tolerance has to act on the WIRE row: a
 * mapper reading `row.stock.symbol` would throw on the one malformed row that
 * `lenientList` exists to drop. Field names are mm-be's
 * (`api/v5/signals/serializers.py`, origin/dev 71ac0d3; the two per-stock
 * reads 653a203), and each schema's output type is the app type, so the two
 * cannot drift.
 *
 * Tolerance, per the wire-schema rule (require only what mm-be guarantees):
 *  - every decimal is a string on the wire and `Money` here; every field the
 *    serializer marks nullable stays nullable;
 *  - row lists go through `lenientList`, so one bad row is dropped and logged
 *    instead of failing its board, and a row whose `signal` is neither
 *    `green` nor `red` is one of those;
 *  - `periods`, `years`, `met` and `unmet` are free strings: no enums, no
 *    lengths, and a `reading` or `rating` code this build does not know is
 *    null rather than a parse failure;
 *  - `data: null` is a normal answer ("nothing for that session"). The
 *    service returns it as `null` and the screen draws its empty state.
 *
 * Dates stay ISO `YYYY-MM-DD`: they round-trip into `?date=` and the cache
 * key, and the screens print them through the house BS formatter.
 */

/** Which board a signal sits on. The API never says buy or sell, and neither does the app. */
export const SIGNAL_SIDES = ['green', 'red'] as const
export type SignalSide = (typeof SIGNAL_SIDES)[number]

/** Breakout finder's two boards: closes past a prior high, or below a prior low. */
export const BREAKOUT_DIRECTIONS = ['above', 'below'] as const
export type BreakoutDirection = (typeof BREAKOUT_DIRECTIONS)[number]

/** The cycle map's two groupings: Nepali months, or fiscal quarters (Q1 = Shrawan–Asoj). */
export const CYCLE_GROUPINGS = ['month', 'quarter'] as const
export type CycleGrouping = (typeof CYCLE_GROUPINGS)[number]

const countSchema = z.number().int().nonnegative()

/**
 * A company name as the register spells it, tidied for display: some carry a
 * trailing space or a doubled one ("NMB  Microfinance…"), and a blank is no
 * name at all.
 */
const displayNameSchema = z
  .string()
  .nullable()
  .transform((value) => blankToNull(value)?.replace(/\s+/g, ' ').trim() ?? null)

/**
 * The stock on every signal row. Index and sector rows ride in the same lists
 * (`NEPSE`, `MANUFACTURE`, `logo: null`), so `name` and `logo` are both
 * optional facts. `logo` is passed through untouched: dev's URLs lack
 * `/media/` (B13), and the avatar's own fallback chain is what copes with it.
 */
export const signalStockSchema = z.object({
  symbol: z.string().min(1),
  name: displayNameSchema,
  logo: z.string().nullable().transform(blankToNull),
  // Optional as well as nullable: mm-be added `sector` after the first rows shipped, and a
  // cached or older answer without the key must still parse.
  sector: z
    .string()
    .nullish()
    .transform((value) => blankToNull(value ?? null)),
})
export type SignalStock = z.infer<typeof signalStockSchema>

// ── Boards: /live/ and /daily/ ───────────────────────────────────────────────

/**
 * One chip: `price` is the run's price, `strength` the rank the API sorts by.
 * `firstSeenAt` is Live's "Signal at": the first run today that showed the
 * stock on this side. Daily sends null, and an older answer omits it.
 */
export const signalRowSchema = z
  .object({
    stock: signalStockSchema,
    signal: z.enum(SIGNAL_SIDES),
    price: moneySchema.nullable(),
    strength: moneySchema.nullable(),
    first_seen_at: z.string().nullable().optional(),
  })
  .transform(({ first_seen_at, ...row }) => ({ ...row, firstSeenAt: first_seen_at ?? null }))
export type SignalRow = z.infer<typeof signalRowSchema>

/**
 * A green and a red board for one session, each strongest first (the API's
 * order, kept). `asOf` is the intraday run's time on `/live/` and always null
 * on `/daily/`.
 */
export const signalBoardSchema = z
  .object({
    date: isoDateSchema,
    as_of: z.string().nullable(),
    green: lenientList(signalRowSchema, 'green signal'),
    red: lenientList(signalRowSchema, 'red signal'),
  })
  .transform(({ as_of, ...board }) => ({ ...board, asOf: as_of }))
export type SignalBoard = z.infer<typeof signalBoardSchema>

/**
 * One intraday run of the session, for the Live run picker: `id` is what
 * `/live/?run=` takes, `at` the run's own time. Oldest first, as sent.
 */
export const liveRunsSchema = lenientList(
  z.object({ id: z.number().int().min(1), at: z.string() }),
  'live run',
)
export type LiveRun = z.infer<typeof liveRunsSchema>[number]

// ── /sessions/ ───────────────────────────────────────────────────────────────

/** The sessions that have signals, newest first: what a session picker may offer. */
export const signalSessionsSchema = lenientList(
  z.object({ date: isoDateSchema }).transform((session) => session.date),
  'signal session',
)

// ── /dashboard/ ──────────────────────────────────────────────────────────────

/**
 * The session's NEPSE figures. `indexChange` is null on dev while the percent
 * is set (B14), so a screen prints the percent alone. `turnover` is rupees.
 */
const signalMarketSchema = z
  .object({
    index_value: moneySchema,
    index_change: moneySchema.nullable(),
    index_change_percent: percentSchema.nullable(),
    volume: z.number().int().nullable(),
    turnover: moneySchema.nullable(),
    stocks_traded: countSchema,
    advances: countSchema,
    declines: countSchema,
    unchanged: countSchema,
  })
  .transform((market) => ({
    indexValue: market.index_value,
    indexChange: market.index_change,
    indexChangePercent: market.index_change_percent,
    volume: market.volume,
    turnover: market.turnover,
    stocksTraded: market.stocks_traded,
    advances: market.advances,
    declines: market.declines,
    unchanged: market.unchanged,
  }))
export type SignalMarket = z.infer<typeof signalMarketSchema>

/**
 * The session's counts and its market block. The market block is the
 * dashboard's secondary half: one that does not parse is drawn as missing
 * (and logged) rather than taking the counts down with it.
 */
export const signalDashboardSchema = z
  .object({
    date: isoDateSchema,
    green: countSchema,
    red: countSchema,
    breaking_out: countSchema,
    breaking_down: countSchema,
    sideways: countSchema,
    market: signalMarketSchema.nullable().catch(({ issues, value }) => {
      console.warn('drew no market block for a malformed one', issues, value)
      return null
    }),
  })
  .transform((dashboard) => ({
    date: dashboard.date,
    green: dashboard.green,
    red: dashboard.red,
    breakingOut: dashboard.breaking_out,
    breakingDown: dashboard.breaking_down,
    sideways: dashboard.sideways,
    market: dashboard.market,
  }))
export type SignalDashboard = z.infer<typeof signalDashboardSchema>

// ── /trend/ ──────────────────────────────────────────────────────────────────

/**
 * Which of the trend checks a stock meets. `total` is the API's own count of
 * checks (13 today) — read it, never assume it. The condition strings are
 * printed exactly as sent, U+2212 minus included.
 */
export const trendRowSchema = z.object({
  stock: signalStockSchema,
  met: z.array(z.string()),
  unmet: z.array(z.string()),
  total: countSchema,
})
export type TrendRow = z.infer<typeof trendRowSchema>

// ── /breakouts/ ──────────────────────────────────────────────────────────────

/** `ltp` is that session's close, not today's price. */
export const levelRowSchema = z.object({
  stock: signalStockSchema,
  ltp: moneySchema.nullable(),
  level: moneySchema.nullable(),
})
export type LevelRow = z.infer<typeof levelRowSchema>

/** One session's breakouts in one direction, A→Z by symbol (the API's order). */
export const levelBoardSchema = z
  .object({
    date: isoDateSchema,
    direction: z.string(),
    rows: lenientList(levelRowSchema, 'breakout'),
  })
  .transform((board) => ({
    ...board,
    direction: knownWord(BREAKOUT_DIRECTIONS, board.direction),
  }))
export type LevelBoard = z.infer<typeof levelBoardSchema>

// ── /volatile/, /steady/, /momentum/ ─────────────────────────────────────────

/** Mean daily (high − low) / low over `sessions` sessions (30, fewer for a newer listing). */
export const swingRowSchema = z
  .object({
    stock: signalStockSchema,
    swing_percent: percentSchema,
    sessions: countSchema,
    ltp: moneySchema.nullable(),
  })
  .transform(({ swing_percent, ...row }) => ({ ...row, swingPercent: swing_percent }))
export type SwingRow = z.infer<typeof swingRowSchema>

/** A stock that held a tight band on the session; `ltp` is that session's close. */
export const steadyRowSchema = z.object({
  stock: signalStockSchema,
  ltp: moneySchema.nullable(),
  low: moneySchema,
  high: moneySchema,
})
export type SteadyRow = z.infer<typeof steadyRowSchema>

/** Highest RSI first (the API's order). `ltp` and `volatilityPercent` can be null (dev: KRBL). */
export const momentumRowSchema = z
  .object({
    stock: signalStockSchema,
    ltp: moneySchema.nullable(),
    rsi: moneySchema.nullable(),
    volatility_percent: percentSchema.nullable(),
  })
  .transform(({ volatility_percent, ...row }) => ({
    ...row,
    volatilityPercent: volatility_percent,
  }))
export type MomentumRow = z.infer<typeof momentumRowSchema>

// ── /open-trades/, /history/ ─────────────────────────────────────────────────

/**
 * A green signal no red one has closed yet. `signalPrice` is the signal day's
 * LOW (owner decision D1), so `sincePercent` is measured from the low, never
 * from the close.
 */
export const openTradeSchema = z
  .object({
    stock: signalStockSchema,
    signal_date: isoDateSchema,
    signal_price: moneySchema.nullable(),
    ltp: moneySchema.nullable(),
    since_percent: percentSchema.nullable(),
  })
  .transform((trade) => ({
    stock: trade.stock,
    signalDate: trade.signal_date,
    signalPrice: trade.signal_price,
    ltp: trade.ltp,
    sincePercent: trade.since_percent,
  }))
export type OpenTrade = z.infer<typeof openTradeSchema>

/**
 * A green → red pair. Entry is the green day's LOW and exit the red day's
 * HIGH (D1), so `returnPercent` is best-case by construction. Most recent
 * exit first (the API's order).
 */
export const closedTradeSchema = z
  .object({
    stock: signalStockSchema,
    entry_date: isoDateSchema,
    entry_price: moneySchema.nullable(),
    exit_date: isoDateSchema,
    exit_price: moneySchema.nullable(),
    return_percent: percentSchema.nullable(),
  })
  .transform((trade) => ({
    stock: trade.stock,
    entryDate: trade.entry_date,
    entryPrice: trade.entry_price,
    exitDate: trade.exit_date,
    exitPrice: trade.exit_price,
    returnPercent: trade.return_percent,
  }))
export type ClosedTrade = z.infer<typeof closedTradeSchema>

// ── /stocks/{symbol}/performance/ ────────────────────────────────────────────

/**
 * The Signal page's twelve figures, as mm-be works them out from the stock's
 * whole history (`services/signals/performance.py`). `totalTrades` counts the
 * CLOSED trades; `openTrades` is 0 or 1, the green no red has closed yet,
 * never a win or a loss. Every other figure is null when there is nothing to
 * divide by (no trades, no losses) and CAGR is null under a year — the page
 * draws each as "Not available yet". `averageHoldingDays` is to one decimal.
 */
export const signalPerformanceStatsSchema = z
  .object({
    total_trades: countSchema,
    profitable_trades: countSchema,
    losing_trades: countSchema,
    open_trades: countSchema,
    accuracy_percent: percentSchema.nullable(),
    average_return_percent: percentSchema.nullable(),
    win_loss_ratio: moneySchema.nullable(),
    profit_factor: moneySchema.nullable(),
    cagr_percent: percentSchema.nullable(),
    max_drawdown_percent: percentSchema.nullable(),
    average_holding_days: moneySchema.nullable(),
    roi_percent: percentSchema.nullable(),
  })
  .transform((stats) => ({
    totalTrades: stats.total_trades,
    profitableTrades: stats.profitable_trades,
    losingTrades: stats.losing_trades,
    openTrades: stats.open_trades,
    accuracyPercent: stats.accuracy_percent,
    averageReturnPercent: stats.average_return_percent,
    winLossRatio: stats.win_loss_ratio,
    profitFactor: stats.profit_factor,
    cagrPercent: stats.cagr_percent,
    maxDrawdownPercent: stats.max_drawdown_percent,
    averageHoldingDays: stats.average_holding_days,
    roiPercent: stats.roi_percent,
  }))
export type SignalPerformanceStats = z.infer<typeof signalPerformanceStatsSchema>

/**
 * One closed signal in the paper account: entered at the green day's LOW,
 * left at the red day's HIGH (D1, a best case), with the whole balance after
 * it and that balance against the starting one.
 */
export const signalTradeSchema = z
  .object({
    entry_date: isoDateSchema,
    entry_price: moneySchema,
    exit_date: isoDateSchema,
    exit_price: moneySchema,
    return_percent: percentSchema,
    balance: moneySchema,
    total_return_percent: percentSchema,
  })
  .transform((trade) => ({
    entryDate: trade.entry_date,
    entryPrice: trade.entry_price,
    exitDate: trade.exit_date,
    exitPrice: trade.exit_price,
    returnPercent: trade.return_percent,
    balance: trade.balance,
    totalReturnPercent: trade.total_return_percent,
  }))
export type SignalTrade = z.infer<typeof signalTradeSchema>

/** The stock's green signal still open, measured from its entry (the day's low) to `ltp`. */
export const signalOpenPositionSchema = z
  .object({
    entry_date: isoDateSchema,
    entry_price: moneySchema,
    ltp: moneySchema.nullable(),
    since_percent: percentSchema.nullable(),
  })
  .transform((position) => ({
    entryDate: position.entry_date,
    entryPrice: position.entry_price,
    ltp: position.ltp,
    sincePercent: position.since_percent,
  }))
export type SignalOpenPosition = z.infer<typeof signalOpenPositionSchema>

/**
 * Every closed signal of one stock since it listed, replayed from
 * `startingBalance` with the whole balance in each, no fees. `trades` is
 * oldest first, as sent: the equity table's rows and the graph's points.
 */
export const signalPerformanceSchema = z
  .object({
    symbol: z.string().min(1),
    starting_balance: moneySchema,
    stats: signalPerformanceStatsSchema,
    trades: lenientList(signalTradeSchema, 'signal trade'),
    open_position: signalOpenPositionSchema.nullable(),
  })
  .transform((performance) => ({
    symbol: performance.symbol,
    startingBalance: performance.starting_balance,
    stats: performance.stats,
    trades: performance.trades,
    openPosition: performance.open_position,
  }))
export type SignalPerformance = z.infer<typeof signalPerformanceSchema>

// ── /stocks/{symbol}/technicals/ ─────────────────────────────────────────────

/** Which way one indicator reads. */
export const INDICATOR_DIRECTIONS = ['up', 'down', 'neutral'] as const
export type IndicatorDirection = (typeof INDICATOR_DIRECTIONS)[number]

/** A gauge's five steps, down to up. */
export const SIGNAL_RATING_STEPS = ['strong_down', 'down', 'neutral', 'up', 'strong_up'] as const
export type SignalRatingStep = (typeof SIGNAL_RATING_STEPS)[number]

/**
 * The words to print: mm-be's own (backend-owned, editable), else the code
 * itself as a word, so `strong_up` with no word reads "Strong up".
 */
const wordFor = (word: string | null, code: string | null): string | null =>
  blankToNull(word) ?? (code === null ? null : sentenceCase(code.replace(/_/g, ' ')))

/**
 * A part of the answer the page can draw without: one that does not parse is
 * drawn as missing (and logged) rather than failing the tables beside it, as
 * the dashboard's market block does.
 */
const missingIfMalformed =
  <T>(fallback: T, label: string) =>
  ({ issues, value }: { issues: unknown; value: unknown }): T => {
    console.warn(`drew no ${label} for a malformed one`, issues, value)
    return fallback
  }

/**
 * One oscillator or moving average. `value` is null without enough history,
 * and `reading` with it; a reading this build does not know is null too, never
 * a parse failure. `word` is what the Reading column prints.
 */
export const signalIndicatorSchema = z
  .object({
    code: z.string().min(1),
    label: z.string().min(1),
    value: moneySchema.nullable(),
    reading: z.string().nullable(),
    word: z.string().nullable(),
  })
  .transform((row) => {
    const reading = knownWord(INDICATOR_DIRECTIONS, row.reading)
    return {
      code: row.code,
      label: row.label,
      value: row.value,
      reading,
      word: wordFor(row.word, reading),
    }
  })
export type SignalIndicator = z.infer<typeof signalIndicatorSchema>

const NO_RATING = { score: null, rating: null, word: null } as const

/**
 * A tab's summary gauge: `score` from −1 (every reading down) to 1 (every one
 * up), the step it falls in and the step's word. All three null when nothing
 * could be read.
 */
export const signalRatingSchema = z
  .object({
    score: moneySchema.nullable(),
    rating: z.string().nullable(),
    word: z.string().nullable(),
  })
  .transform((gauge) => {
    const rating = knownWord(SIGNAL_RATING_STEPS, gauge.rating)
    return { score: gauge.score, rating, word: wordFor(gauge.word, rating) }
  })
export type SignalRating = z.infer<typeof signalRatingSchema>

/** One method's seven monthly levels, supports below the pivot, resistances above. */
export const signalPivotLevelsSchema = z.object({
  s3: moneySchema,
  s2: moneySchema,
  s1: moneySchema,
  p: moneySchema,
  r1: moneySchema,
  r2: moneySchema,
  r3: moneySchema,
})
export type SignalPivotLevels = z.infer<typeof signalPivotLevelsSchema>

/**
 * The monthly pivots: built from `month` (its first day, ISO), read against
 * `price` (the close the gauge's marker sits at), in four methods.
 */
export const signalPivotsSchema = z.object({
  month: isoDateSchema,
  price: moneySchema.nullable(),
  rating: signalRatingSchema,
  classic: signalPivotLevelsSchema,
  fibonacci: signalPivotLevelsSchema,
  camarilla: signalPivotLevelsSchema,
  woodie: signalPivotLevelsSchema,
})
export type SignalPivots = z.infer<typeof signalPivotsSchema>

/**
 * The Technicals page's three tabs for one stock, as of the session `asOf`
 * at the close `close`. Rows in the API's order. A listed stock with too
 * little history is an answer: empty rows, null gauges, no pivots.
 */
export const signalTechnicalsSchema = z
  .object({
    symbol: z.string().min(1),
    as_of: isoDateSchema.nullable(),
    close: moneySchema.nullable(),
    oscillators: lenientList(signalIndicatorSchema, 'oscillator'),
    oscillator_rating: signalRatingSchema.catch(missingIfMalformed(NO_RATING, 'oscillator gauge')),
    moving_averages: lenientList(signalIndicatorSchema, 'moving average'),
    moving_average_rating: signalRatingSchema.catch(
      missingIfMalformed(NO_RATING, 'moving-average gauge'),
    ),
    pivots: signalPivotsSchema.nullable().catch(missingIfMalformed(null, 'pivots')),
  })
  .transform((technicals) => ({
    symbol: technicals.symbol,
    asOf: technicals.as_of,
    close: technicals.close,
    oscillators: technicals.oscillators,
    oscillatorRating: technicals.oscillator_rating,
    movingAverages: technicals.moving_averages,
    movingAverageRating: technicals.moving_average_rating,
    pivots: technicals.pivots,
  }))
export type SignalTechnicals = z.infer<typeof signalTechnicalsSchema>

// ── /stocks/{symbol}/setup/ ──────────────────────────────────────────────────

/**
 * The newest setup still open on one stock: where the signal day closed (`entry`) and the levels
 * mm-be calculates from it. Calculated levels, not a recommendation. `stop` and `target` are null
 * when no method found one, and so are the two percentages and their ratio. Only what the rail
 * draws is read: `past[]` and `record[]` on the same payload have no frame yet.
 */
export const signalSetupCaseSchema = z
  .object({
    label: z.string().min(1),
    signal_date: isoDateSchema,
    entry: moneySchema,
    stop: moneySchema.nullable(),
    target: moneySchema.nullable(),
    reward_risk: moneySchema.nullable(),
  })
  .transform((setup) => ({
    label: setup.label,
    signalDate: setup.signal_date,
    entry: setup.entry,
    stop: setup.stop,
    target: setup.target,
    rewardRisk: setup.reward_risk,
  }))
export type SignalSetupCase = z.infer<typeof signalSetupCaseSchema>

/** `setup` is null for most stocks: no signal is open on them. A malformed one reads as none. */
export const signalSetupSchema = z.object({
  setup: signalSetupCaseSchema.nullable().catch(missingIfMalformed(null, 'signal setup')),
})
export type SignalSetup = z.infer<typeof signalSetupSchema>

// ── /cycle-map/ ──────────────────────────────────────────────────────────────

/**
 * One sector's average return per period. A `null` cell means no year had
 * data, not 0%; `"-0.00"` arrives as sent and is the screen's to print as 0.
 * `name` is the plain sector name, which is a `shortSectorLabel` key.
 */
export const cycleRowSchema = z.object({
  symbol: z.string().min(1),
  name: displayNameSchema,
  changes: z.array(percentSchema.nullable()),
})
export type CycleRow = z.infer<typeof cycleRowSchema>

/** `years` lists the BS years that contributed, which can be fewer than asked for. */
export const cycleMapSchema = z
  .object({
    by: z.string(),
    periods: z.array(z.string()),
    years: z.array(z.string()),
    rows: lenientList(cycleRowSchema, 'cycle-map sector'),
  })
  .transform((map) => ({ ...map, by: knownWord(CYCLE_GROUPINGS, map.by) }))
export type CycleMap = z.infer<typeof cycleMapSchema>

// ── List envelopes ───────────────────────────────────────────────────────────

/**
 * A signals list's `meta`. Only `count` is in the OpenAPI, so it is the one
 * field required; the paging fields and `date` (trend's and steady's echo of
 * the session) are sent but undocumented (B6), so each is optional and the
 * service falls back to what it asked for.
 */
export const signalsListMetaSchema = z.object({
  count: countSchema,
  page: z.number().int().min(1).optional(),
  page_size: z.number().int().min(1).optional(),
  total: countSchema.optional(),
  total_pages: countSchema.optional(),
  date: isoDateSchema.nullable().optional(),
})

/** One server page of a signals list, with the session it was computed for when the API says. */
export interface SignalsPage<Row> {
  rows: Row[]
  page: number
  pageSize: number
  total: number
  totalPages: number
  /** The session the rows belong to (trend, steady); null when the API names none. */
  date: string | null
}

/** An unpaged signals list (open trades): every row in one answer. */
export interface SignalsList<Row> {
  rows: Row[]
  count: number
}

// ── Queries ──────────────────────────────────────────────────────────────────
//
// What each read is asked with. `null` sends no parameter, so mm-be picks its
// own default (the latest computed session) rather than being asked for "".

export interface SignalsPaging {
  page: number
  pageSize: number
}

/** Volatile's read: a page, and the symbol prefix once mm-be takes one (`VOLATILE_SEARCH_AVAILABLE`). */
export interface VolatileQuery extends SignalsPaging {
  /** A symbol PREFIX, case-blind, ≤ 20 characters. Omitted while the endpoint has no `q`. */
  q?: string | null | undefined
}

export interface TrendQuery extends SignalsPaging {
  date: string | null
  /** A symbol PREFIX, case-blind, ≤ 20 characters. */
  q: string | null
}

export interface BreakoutsQuery {
  direction: BreakoutDirection
  date: string | null
}

export interface SteadyQuery extends SignalsPaging {
  date: string | null
}

export interface HistoryQuery extends SignalsPaging {
  q: string | null
}

export interface CycleMapQuery {
  by: CycleGrouping
  /** 1–10 BS years. */
  years: number
}
